<template>
  <div v-if="initialLoadComplete">
    <v-data-table
      :headers="headers"
      :items="filteredPrimaryRoleQuestionList"
      :sort-by.sync="sortBy"
      class="och-table pb-2"
      disable-pagination
      hide-default-footer
    >
      <template v-slot:no-data>
        <i>No questions created for this primary role</i>
      </template>
      <template v-slot:item="{ item }">
        <tr>
          <td>
            <v-btn
              @click.stop="sortUp(item)"
              :disabled="item.orderNumber <= 1"
              icon
              small
              ><v-icon>fa fa-caret-up</v-icon></v-btn
            >{{ item.orderNumber }}
            <v-btn
              @click.stop="sortDown(item)"
              :disabled="
                item.orderNumber >= filteredPrimaryRoleQuestionList.length
              "
              icon
              small
              ><v-icon>fa fa-caret-down</v-icon></v-btn
            >
          </td>
          <td>
            <a
              href=""
              @click="
                $router.push({
                  name: 'ochinUserSecurityQuestionForm',
                  params: { questionId: item.secQuestion.id },
                })
              "
            >
              {{ item.secQuestion.text }}
            </a>
            <span class="font-italic ml-2" v-if="item.secQuestion.isDeprecated"
              >(deprecated)</span
            >
          </td>
          <td>
            {{ filterAnswerList("secQuestionId", item.secQuestion.id).length }}
          </td>
          <td class="text-right">
            <v-btn icon small @click="remove(item.id)"
              ><v-icon small>fas fa-trash</v-icon></v-btn
            >
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>
<script>
import { mapActions, mapState, mapGetters } from "vuex";

export default {
  computed: {
    filteredPrimaryRoleQuestionList() {
      const filteredList = this.filterPrimaryRoleQuestionList(
        "secRolePrimaryId",
        this.primaryRoleId,
        [{ entity: "secQuestion", attribute: "secQuestionId" }]
      );
      filteredList.sort((a, b) => (a.orderNumber > b.orderNumber ? 1 : -1));
      return filteredList;
    },
    filteredQuestionList() {
      return this.filterQuestionList("secRolePrimaryId", this.primaryRoleId);
    },
    primaryRoleId() {
      return parseInt(this.$route.params.primaryRoleId, 10);
    },
    sortedIdList() {
      // This sorted array of ids is used to store the original sort order which is compared to the new sort order resorting the list.
      if (this.filteredPrimaryRoleQuestionList.length) {
        const idList = this.filteredPrimaryRoleQuestionList.map(val => {
          return val.id;
        });
        return idList;
      }
      return [];
    },
    ...mapGetters({
      filterAnswerList: "secAnswer/filterByAttributeValue",
      filterPrimaryRoleQuestionList:
        "secRolePrimarySecQuestion/filterByAttributeValue",
      filterQuestionList: "secQuestion/filterByAttributeValue",
    }),
    ...mapState({
      answerList: state => state.secAnswer.list,
      primaryRoleQuestionList: state => state.secRolePrimarySecQuestion.list,
      questionList: state => state.secQuestion.list,
    }),
  },
  async created() {
    try {
      await Promise.all([
        this.getAnswerList(),
        this.getPrimaryRoleQuestionList(),
        this.getQuestionList(),
      ]);
      this.initialLoadComplete = true;
    } catch (e) {
      console.error(e);
      this.initialLoadError({ type: "error" });
    }
  },
  data() {
    return {
      headers: [
        { text: "Order", value: "orderNumber", sortable: false },
        {
          text: "Question Text",
          value: "text",
          align: "start",
        },
        {
          text: "Answer Count",
          value: "",
          align: "start",
          sortable: false,
        },
        {
          text: " ",
          value: "",
          align: "start",
          sortable: false,
        },
      ],
      initialLoadComplete: false,
      search: null,
      sortBy: "orderNumber",
    };
  },
  methods: {
    async remove(id) {
      try {
        await this.removePrimaryRoleQuestion(id);
      } catch (e) {
        console.error(e);
        this.primaryRoleQuestionRemovalError();
      }
    },
    reorderList(arr, from, to) {
      const item = arr[from];
      arr.splice(from, 1);
      arr.splice(to, 0, item);
      return arr;
    },
    async saveSortOrder(resortedIdList) {
      const changedIndexList = resortedIdList.filter(val => {
        if (resortedIdList.indexOf(val) !== this.sortedIdList.indexOf(val)) {
          return val;
        }
      });

      const promiseArray = changedIndexList.map(async val => {
        return await this.updatePrimaryRoleQuestion([
          val,
          { orderNumber: resortedIdList.indexOf(val) + 1 },
        ]);
      });

      try {
        await Promise.all(promiseArray);
      } catch (e) {
        console.error(e);
        this.notifyOrderUpdateError();
      }

      try {
        await this.getPrimaryRoleQuestionList();
      } catch (e) {
        console.error(e);
        this.notifyFetchPrimaryRoleQuestionListError();
      }
    },
    sortDown(item) {
      if (item.orderNumber >= this.filteredPrimaryRoleQuestionList.length) {
        return;
      }
      const resortedIdList = this.sortedIdList.slice();
      const fromIndex = resortedIdList.indexOf(item.id);
      const toIndex = fromIndex + 1;

      this.reorderList(resortedIdList, fromIndex, toIndex);
      this.saveSortOrder(resortedIdList);
    },
    sortUp(item) {
      if (item.orderNumber <= 1) {
        return;
      }
      const resortedIdList = this.sortedIdList.slice();
      const fromIndex = resortedIdList.indexOf(item.id);
      const toIndex = fromIndex - 1;

      this.reorderList(resortedIdList, fromIndex, toIndex);
      this.saveSortOrder(resortedIdList);
    },
    ...mapActions({
      getAnswerList: "secAnswer/list",
      getPrimaryRoleQuestionList: "secRolePrimarySecQuestion/list",
      getQuestionList: "secQuestion/list",
      removePrimaryRoleQuestion: "secRolePrimarySecQuestion/remove",
      updatePrimaryRoleQuestion: "secRolePrimarySecQuestion/update",
    }),
  },
  notifications: {
    initialLoadError: {
      message: "Could not load initial page state. Refresh page and try again.",
      timeout: 0,
    },
    notifyFetchPrimaryRoleQuestionListError: {
      message: "Could not fetch the question list for this primary role.",
    },
    notifyOrderUpdateError: {
      message: "Could not update the question order.",
    },
    primaryRoleQuestionRemovalError: {
      message: "Could not remove question from primary role.",
    },
  },
};
</script>
